// Attribution Tag Types
// Based on AVI CRO Service API v1

// Status enum - simplified to Active/Inactive
export type AttributionTagStatus = 'Active' | 'Inactive';

// Period types for date filtering
export type PeriodType = 'day' | 'week' | 'month' | '30days' | '60days' | '90days' | 'custom';

// Core Attribution Tag (API Response)
export interface AttributionTag {
  hashtag_id: number;
  hashtag_text: string;
  description: string | null;
  status: AttributionTagStatus;
  metadata: Record<string, unknown> | null;
  is_tracked: boolean; // Always true for attribution tags
  created_at: string;
  updated_at: string;
  created_by: string | null;
  // Aggregated metrics (when included in list responses)
  post_count?: number;
  total_emv?: number;
  account_count?: number;
}

// Unified hashtag detail response (GET /hashtags/:hashtagId)
// Works for both attribution tags (is_tracked=true) and discovered hashtags (is_tracked=false)
export interface UnifiedHashtagDetail {
  hashtag_id: number;
  hashtag_text: string;
  description: string | null;
  status: AttributionTagStatus | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  is_tracked: boolean;

  // Aggregated metrics (included in GET /hashtags/:tagId, omitted in GET /hashtags/:tagId/basic)
  metrics?: {
    post_count: number;
    total_emv: number;
    total_engagement: number;
    total_likes: number;
    total_comments: number;
    total_shares: number;
    total_views: number;
    total_saves: number;
    avg_emv_per_post: number;
    account_count: number;
  };

  // Associated accounts - DEPRECATED: No longer populated (hashtag_accounts removed)
  accounts?: TagAccount[];
}

// Deprecated: Use UnifiedHashtagDetail instead
// Keeping for backward compatibility during migration
export interface AttributionTagDetail extends AttributionTag {
  metrics: AttributionTagMetrics;
  accounts?: TagAccount[];
}

// Deprecated: Use UnifiedHashtagDetail.metrics instead
export interface AttributionTagMetrics {
  hashtag_id: number;
  hashtag_text: string;
  description: string | null;
  status: AttributionTagStatus;
  total_posts: number;
  total_emv: number;
  avg_emv_per_post: number;
  total_engagement: number;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  total_views: number;
  total_saves: number;
  account_count: number;
  earliest_post_date: string | null;
  latest_post_date: string | null;
}

// Account associated with a tag
export interface TagAccount {
  id: string;
  partition_key: number;
  platform_id: number;
  username: string;
  display_name: string | null;
  profile_pic_url: string | null;
  follower_count: number;
  is_active: boolean;
}

// Post attributed to a tag
export interface TagPost {
  partition_key: number;
  post_id: number;
  account_id: string;
  platform_id: number;
  caption: string | null;
  post_url: string;
  posted_at: string | null;
  emv: number;
  likes_count: number;
  comments_count: number;
  shares_count: number;
  views_count: number;
}

// Discovered hashtag (from list endpoint)
export interface DiscoveredHashtag {
  hashtag_id: number;
  hashtag_text: string;
  description?: string | null;
  status?: AttributionTagStatus | null;
  post_count: number;
  total_emv: number;
  total_engagement: number;
  latest_post_date: string | null;
  earliest_post_date: string | null;
  is_tracked: boolean; // true = attribution tag, false = discovered hashtag
  account_count: number;
  created_at?: string;
  updated_at?: string;
}

// Detailed hashtag (from single hashtag endpoint /hashtags/:hashtagText)
export interface HashtagDetail {
  hashtag_id: number;
  hashtag_text: string;
  description?: string | null;
  status?: AttributionTagStatus | null;
  is_tracked: boolean; // true = attribution tag, false = discovered hashtag
  post_count: number;
  total_emv: number;
  total_engagement: number;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  total_views: number;
  total_saves: number;
  latest_post_date: string | null;
  earliest_post_date: string | null;
  account_count: number;
  created_at?: string;
  updated_at?: string;
}

// Hashtag type filter
export type HashtagType = 'all' | 'attribution' | 'discovered';

// Unified hashtag item (for combined list view)
export interface UnifiedHashtag {
  id: string;
  hashtag_text: string;
  type: 'attribution' | 'discovered';
  account_count: number;
  post_count: number;
  total_emv: number;
  total_engagement: number;
  status?: AttributionTagStatus;
  description?: string | null;
  created_at?: string;
  updated_at?: string;
  // Original reference IDs
  tag_id?: string;       // For attribution tags
  hashtag_id?: number;   // For discovered hashtags
}

// Request DTOs
export interface CreateAttributionTagRequest {
  customerId?: string; // Added by store before API call
  tag_name: string; // API expects tag_name in request
  description?: string;
  status?: AttributionTagStatus;
  metadata?: Record<string, unknown>;
  account_ids: string[];
}

export interface UpdateAttributionTagRequest {
  description?: string | null;
  status?: AttributionTagStatus;
  metadata?: Record<string, unknown>;
}

export interface ListTagsQuery {
  status?: AttributionTagStatus;
  search?: string;
  account_id?: string;
  sort_by?: 'emv' | 'posts' | 'created_at' | 'name';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
}

export interface TagPostsQuery {
  limit?: number;
  offset?: number;
  period?: PeriodType;
  customStartDate?: string;
  customEndDate?: string;
}

export interface TagMetricsQuery {
  start_date?: string;
  end_date?: string;
}

export interface DiscoveredHashtagsQuery {
  search?: string;
  min_emv?: number;
  min_posts?: number;
  sort_by?: 'emv' | 'posts' | 'recent';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  period?: PeriodType;
  customStartDate?: string;
  customEndDate?: string;
}

// Query for single hashtag detail/posts
export interface HashtagQuery {
  period?: PeriodType;
  customStartDate?: string;
  customEndDate?: string;
}

export interface HashtagPostsQuery {
  limit?: number;
  offset?: number;
  period?: PeriodType;
  customStartDate?: string;
  customEndDate?: string;
}

// Unified hashtags query (for combined list view)
export interface UnifiedHashtagsQuery {
  type?: HashtagType;
  search?: string;
  status?: AttributionTagStatus;
  sort_by?: 'emv' | 'posts' | 'recent' | 'name';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  period?: PeriodType;
  start_date?: string;
  end_date?: string;
}

export interface PromoteHashtagRequest {
  customerId?: string; // Added by store before API call
  hashtag_text: string; // API expects hashtag_text for promotion endpoint
  description?: string;
  status?: AttributionTagStatus;
  metadata?: Record<string, unknown>;
  account_ids: string[];
}

export interface AddAccountsRequest {
  customerId?: string; // Added by store before API call
  account_ids: string[];
}

// Response types
export interface AttributionTagResponse {
  success: boolean;
  data: AttributionTag;
  message?: string;
}

// Unified hashtag detail response (GET /hashtags/:hashtagId)
export interface UnifiedHashtagDetailResponse {
  success: boolean;
  data: UnifiedHashtagDetail;
}

// Deprecated: Use UnifiedHashtagDetailResponse instead
export interface AttributionTagDetailResponse {
  success: boolean;
  data: AttributionTagDetail;
}

export interface AttributionTagListResponse {
  success: boolean;
  data: AttributionTag[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface TagAccountsResponse {
  success: boolean;
  data: TagAccount[];
}

export interface TagPostsResponse {
  success: boolean;
  data: TagPost[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
}

export interface TagMetricsResponse {
  success: boolean;
  data: AttributionTagMetrics;
}

export interface DiscoveredHashtagsResponse {
  success: boolean;
  data: DiscoveredHashtag[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  dateRange: {
    start: string;
    end: string;
    period: PeriodType;
  };
}

// Single hashtag detail response (GET /hashtags/:hashtagText)
export interface HashtagDetailResponse {
  success: boolean;
  data: HashtagDetail;
  dateRange: {
    start: string;
    end: string;
    period: PeriodType;
  };
}

// Hashtag posts response (GET /hashtags/:hashtagText/posts)
export interface HashtagPostsResponse {
  success: boolean;
  data: TagPost[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  dateRange: {
    start: string;
    end: string;
    period: PeriodType;
  };
}

// Unified hashtags response (for combined list view)
export interface UnifiedHashtagsResponse {
  success: boolean;
  data: UnifiedHashtag[];
  pagination: {
    limit: number;
    offset: number;
    total: number;
  };
  dateRange?: {
    start: string;
    end: string;
    period: PeriodType;
  };
}

export interface TagPickerItem {
  hashtag_id: number;
  hashtag_text: string;
  description: string | null;
}

export interface TagPickerResponse {
  success: boolean;
  data: TagPickerItem[];
}

// Status colors for badges
export const STATUS_COLORS: Record<AttributionTagStatus, { bg: string; text: string; border: string }> = {
  Active: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-200' },
  Inactive: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-200' },
};

// All available status options
export const STATUS_OPTIONS: AttributionTagStatus[] = ['Active', 'Inactive'];

// Helper to get available status options (simplified - all options always available)
export const getAvailableStatusOptions = (
  _currentStatus?: AttributionTagStatus
): AttributionTagStatus[] => {
  return STATUS_OPTIONS;
};

// ==========================================
// Tag Grouping
// ==========================================

// A member tag within a group
export interface TagGroupMember {
  hashtag_id: number;
  hashtag_text: string;
}

// Aggregate metrics for a group (deduped across member variants, canonical EMV)
export interface TagGroupMetrics {
  post_count: number;
  total_emv: number;
  avg_emv_per_post: number;
  total_views: number;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  total_saves: number;
  total_engagement: number;
  account_count: number;
  latest_post_date: string | null;
}

// A tag group (GET /attribution-tags/groups[/:id])
export interface TagGroup {
  id: string;
  customer_id: string;
  name: string;
  created_at: string;
  updated_at: string;
  members: TagGroupMember[];
  metrics?: TagGroupMetrics; // included by the detail endpoint
}

// A single entry in the attribution list — either a collapsed GROUP or a solo TAG.
// (GET /hashtags?type=attribution now returns a mix of these.)
export interface AttributionListEntry {
  kind: 'group' | 'tag';
  effective_tag_id: string; // group_id (group) or the attribution-tag uuid (solo)
  group_id: string | null; // set when kind === 'group'
  hashtag_id: number | null; // set when kind === 'tag'
  hashtag_text: string; // label: group name or the tag's text
  name: string; // alias of hashtag_text (group name / tag text)
  member_count: number; // # variants in a group; 1 for a solo tag
  description: string | null;
  status: AttributionTagStatus | null;
  is_attribution_tag: boolean;
  post_count: number;
  total_emv: number;
  total_views: number;
  total_likes: number;
  total_comments: number;
  total_shares: number;
  total_engagement: number;
  account_count: number;
  latest_post_date: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AttributionListResponse {
  success: boolean;
  data: AttributionListEntry[];
  total?: number;
}

// Response wrappers
export interface TagGroupResponse {
  success: boolean;
  data: TagGroup;
  message?: string;
}
export interface TagGroupListResponse {
  success: boolean;
  data: TagGroup[];
  total: number;
}
